import { QRCodeSVG } from "qrcode.react";
import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as ArrowDown } from "../../../../assets/svg/arrow-down.svg";
import { ReactComponent as ArrowOut } from "../../../../assets/svg/arrow-up-right.svg";
import { ReactComponent as BlockExplorer } from "../../../../assets/svg/block-explorer.svg";
import { ReactComponent as Copy } from "../../../../assets/svg/copy.svg";
import { ReactComponent as Dollar } from "../../../../assets/svg/dollar.svg";
import { ReactComponent as Disconnect } from "../../../../assets/svg/power.svg";
import { ReactComponent as Refresh } from "../../../../assets/svg/refresh.svg";
import { ReactComponent as ArrowRepeat } from "../../../../assets/svg/repeat.svg";
import { ReactComponent as Settings } from "../../../../assets/svg/settings.svg";
import { ReactComponent as StopSync } from "../../../../assets/svg/stop-circle.svg";
import { ReactComponent as Subaccounts } from "../../../../assets/svg/subaccounts.svg";
import { FeatureFlagKey, isFeatureEnabled } from "../../../../config/features";
import {
  GetAccount200Response,
  GetTransactionHistory200ResponseTransactionHistoryInner,
} from "../../../../codegen-api";
import { COLORS } from "../../../../constants/design/colors";
import {
  PWA_UI_ENABLED,
  TRANSFER_UI_ENABLED,
  USER_SETTINGS_UI_ENABLED,
} from "../../../../constants/featureFlags";
import {
  AccountStateEnum,
} from "../../../../contexts/AuthContext";
import { isPWA } from "../../../../utils/pwa";
import { formatKnownValue } from "../../../../utils/unknownNumbers";
import { Button, ButtonThemeEnum } from "../../../Buttons/styles";
import {
  AccountActionRow,
  AccountItemRow,
  AccountItemsContainer,
  BalanceValue,
  Content,
  DepositItemsContainer,
  EnableTradingButton,
  ExchangeBalanceWrapper,
  WalletQrCode,
} from "./style";

interface IAccountModalContentProps {
  account?: string | null;
  accountData?: GetAccount200Response;
  isAccountImported: boolean;
  showCopiedAddress: boolean;
  onCopyWalletAddress: () => void;
  onDeposit: () => void;
  onWithdraw: () => void;
  onSubaccountsClick?: () => void;
  onTransfer: () => void;
  openBlockExplorer: () => void;
  txHistory: GetTransactionHistory200ResponseTransactionHistoryInner[];
  setShowTransfersList: Dispatch<SetStateAction<boolean>>;
  accountApiKeyState: AccountStateEnum;
  isMobileScreen: boolean;
  onSyncPWA?: () => void;
  openSettings: () => void;
  onDisconnect: () => void;
  showOnboarding: boolean | undefined;
  onHide?: () => void;
  onContinueOnboarding?: () => void;
}

function AccountModalContent({
  account,
  accountData,
  isAccountImported,
  showCopiedAddress,
  onCopyWalletAddress,
  onDeposit,
  onWithdraw,
  onSubaccountsClick,
  onTransfer,
  openBlockExplorer,
  txHistory,
  setShowTransfersList,
  accountApiKeyState,
  isMobileScreen,
  onSyncPWA,
  openSettings,
  onDisconnect,
  showOnboarding,
  onHide,
  onContinueOnboarding,
}: IAccountModalContentProps) {
  const { t } = useTranslation("app", { keyPrefix: "Header.AccountModal" });

  return (
    <Content>
      <ExchangeBalanceWrapper>
        <span className="pb-4">{t("deposit")}</span>
        {account ? (
          <WalletQrCode aria-label={t("wallet_qr_code")}>
            <QRCodeSVG
              value={account}
              size={104}
              bgColor="#ffffff"
              fgColor="#111111"
              level="M"
            />
          </WalletQrCode>
        ) : null}
        <span>{t("equity_balance")}</span>
        <BalanceValue>
          {accountData ? formatKnownValue(accountData.equity) : "---"}
        </BalanceValue>
      </ExchangeBalanceWrapper>
      <DepositItemsContainer>
        {accountData?.in_liquidation ? (
          <Button disabled buttonTheme={ButtonThemeEnum.NEGATIVE}>
            {t("liquidations_in_progress")}
          </Button>
        ) : isAccountImported ? (
          <Button disabled buttonTheme={ButtonThemeEnum.NEUTRAL2}>
            {t("import_account_deposits_withdrawals_not_allowed")}
          </Button>
        ) : (
          <>
            {/* Row 1 — utility actions (copy / subaccounts / transfer) */}
            <AccountActionRow>
              <Button
                buttonTheme={ButtonThemeEnum.NEUTRAL1}
                onClick={onCopyWalletAddress}
              >
                <Copy />
                {showCopiedAddress ? t("copied_address") : t("copy_address")}
              </Button>
              {/* onSubaccountsClick is only passed on chains that support
                  subaccounts; the flag keeps the entry off until it is launched. */}
              {onSubaccountsClick && isFeatureEnabled(FeatureFlagKey.SUBACCOUNTS_UI) && (
                <Button
                  buttonTheme={ButtonThemeEnum.NEUTRAL1}
                  onClick={onSubaccountsClick}
                >
                  <Subaccounts />
                  {t("subaccounts")}
                </Button>
              )}
              {/* Transfer disabled from UI/UX — flip TRANSFER_UI_ENABLED to re-enable */}
              {TRANSFER_UI_ENABLED && (
                <Button
                  buttonTheme={ButtonThemeEnum.NEUTRAL1}
                  onClick={onTransfer}
                >
                  <ArrowRepeat />
                  {t("transfer")}
                </Button>
              )}
            </AccountActionRow>
            {/* Row 2 — primary money actions */}
            <AccountActionRow>
              <Button
                buttonTheme={ButtonThemeEnum.NEUTRAL1}
                onClick={onDeposit}
              >
                <ArrowDown />
                {t("deposit")}
              </Button>
              <Button
                buttonTheme={ButtonThemeEnum.NEUTRAL1}
                onClick={onWithdraw}
              >
                <ArrowDown style={{ rotate: "180deg" }} />
                {t("withdraw")}
              </Button>
            </AccountActionRow>
          </>
        )}
      </DepositItemsContainer>

      <AccountItemsContainer>
        <AccountItemRow onClick={openBlockExplorer}>
          <BlockExplorer />
          <span>{t("open_block_explorer")}</span>
          <ArrowOut />
        </AccountItemRow>
        {txHistory.length > 0 ? (
          <AccountItemRow
            isHighlighted
            onClick={() => setShowTransfersList(true)}
          >
            <Dollar />
            <span>{t("transfers_in_progress")}</span>
          </AccountItemRow>
        ) : null}

        {/* PWA disabled from UI/UX — flip PWA_UI_ENABLED to re-enable */}
        {PWA_UI_ENABLED &&
          (accountApiKeyState !== AccountStateEnum.OK || !isMobileScreen) && (
            <AccountItemRow onClick={onSyncPWA}>
              <Refresh stroke={COLORS.neutral.one} />
              <span>{t("sync_with_pwa")}</span>
            </AccountItemRow>
          )}
        {/* User Settings disabled from UI/UX — flip USER_SETTINGS_UI_ENABLED to re-enable */}
        {USER_SETTINGS_UI_ENABLED && (
          <AccountItemRow onClick={openSettings}>
            <Settings />
            <span>{t("user_settings")}</span>
          </AccountItemRow>
        )}
        {isPWA && isAccountImported ? (
          <AccountItemRow onClick={onDisconnect}>
            <StopSync />
            <span>{t("stop_syncing")}</span>
          </AccountItemRow>
        ) : (
          <AccountItemRow onClick={onDisconnect}>
            <Disconnect />
            <span>{t("disconnect")}</span>
          </AccountItemRow>
        )}
        {(accountApiKeyState !== AccountStateEnum.OK || showOnboarding) &&
          !isAccountImported && (
            <EnableTradingButton
              onClick={() => {
                onHide?.();
                onContinueOnboarding?.();
              }}
            >
              {
                // eslint-disable-next-line no-nested-ternary
                accountApiKeyState === AccountStateEnum.REQUIRE_PASSWORD
                  ? t("unlock_trading")
                  : accountApiKeyState ===
                    AccountStateEnum.REQUIRE_REGISTER_SIGNING
                    ? t("complete_sign_in")
                    : t("continue_onboarding")
              }
            </EnableTradingButton>
          )}
      </AccountItemsContainer>
    </Content>
  );
}

export default AccountModalContent;
